import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  fetchRolePositions,
  PositionRef,
  setPositionRoles,
} from "services/positions.service";
import { usePositionsCatalogue } from "./usePositionsCatalogue";
import { filterPositions, positionRoleIdsWith } from "./positions-helpers";
import { describePositionsError } from "./positions-errors";

/**
 * «Pareigos» on a pareigybė page: the positions that bring this pareigybė.
 * Managers of the pareigybė's sphere may link/unlink positions; a change
 * applies at once to every current holder of the position (rule 11 — the
 * editor is the approver), except holders with a pending approval in that
 * sphere, whose approval computes the proposal live.
 */
type Props = {
  companyId: string;
  roleId: string;
  canEdit: boolean;
};

export default function RolePositionsCard({ companyId, roleId, canEdit }: Props) {
  const { t } = useTranslation();
  const [linked, setLinked] = useState<PositionRef[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const requestRef = useRef(0);
  // The catalogue (with each position's current roles) is only needed to edit.
  const catalogue = usePositionsCatalogue(editing ? companyId : undefined);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoadFailed(false);
    try {
      const rows = await fetchRolePositions(companyId, roleId);
      if (requestId === requestRef.current) setLinked(rows);
    } catch {
      if (requestId === requestRef.current) {
        setLinked([]);
        setLoadFailed(true);
      }
    }
  }, [companyId, roleId]);

  useEffect(() => {
    setLinked(null);
    setEditing(false);
    load();
  }, [load]);

  const linkedIds = useMemo(() => new Set((linked || []).map((p) => p.id)), [linked]);
  const candidates = useMemo(() => filterPositions(catalogue.active, query), [catalogue.active, query]);

  const toggle = async (positionId: string) => {
    const position = catalogue.byId.get(positionId);
    if (!position) return;
    const link = !linkedIds.has(positionId);
    setBusyId(positionId);
    try {
      await setPositionRoles(companyId, positionId, positionRoleIdsWith(position, roleId, link));
      await load();
      catalogue.reload();
    } catch (e: any) {
      toast.error(describePositionsError(e, t));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
      <div className="mb-4 flex items-center justify-between border-b border-zinc-100 pb-3 dark:border-zinc-800">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
          {t("positions_title")}
          {linked && linked.length > 0 && (
            <span className="ml-2 font-normal text-zinc-400">({linked.length})</span>
          )}
        </h3>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing((value) => !value)}
            className="cursor-pointer border-0 bg-transparent text-xs font-semibold text-blue-600 hover:text-blue-800 max-md:min-h-11 max-md:text-sm"
          >
            {editing ? t("close") : t("edit")}
          </button>
        )}
      </div>

      {linked === null ? (
        <p className="text-sm text-zinc-400">{t("loading")}</p>
      ) : loadFailed ? (
        <p className="text-sm text-red-700">{t("positions_load_failed")}</p>
      ) : linked.length === 0 ? (
        <p className="text-sm italic text-zinc-400">{t("positions_role_none")}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {linked.map((position) => (
            <span
              key={position.id}
              className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-sm text-blue-900 ring-1 ring-inset ring-blue-200"
            >
              {position.nameLT}
            </span>
          ))}
        </div>
      )}

      {editing && (
        <div className="mt-4 space-y-2">
          <p className="text-xs text-zinc-500">{t("positions_role_link_hint")}</p>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("positions_picker_search")}
            aria-label={t("positions_picker_search")}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
          />
          <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
            {catalogue.loading && catalogue.positions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">{t("loading")}</div>
            ) : catalogue.error ? (
              <div className="px-3 py-2 text-sm text-red-700">{t("positions_load_failed")}</div>
            ) : candidates.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">{t("positions_picker_empty")}</div>
            ) : (
              candidates.map((position) => (
                <label
                  key={position.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 max-md:py-3"
                >
                  <input
                    type="checkbox"
                    checked={linkedIds.has(position.id)}
                    // A position's role list is replaced wholesale: wait for
                    // the refreshed catalogue before the next change.
                    disabled={busyId !== null || catalogue.loading}
                    onChange={() => toggle(position.id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 max-md:h-5 max-md:w-5"
                  />
                  <span className="min-w-0 flex-1 truncate text-gray-900">{position.nameLT}</span>
                  <span className="shrink-0 text-xs text-gray-500">
                    {t("positions_holders_count", { count: position.holdersCount })}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
